import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Faltan las variables de entorno de Supabase");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper para obtener el tenant_id del usuario actual
// Se usa en operaciones INSERT para agregar tenant_id automáticamente
let _cachedTenantId: string | null = null;

export async function getCurrentTenantId(): Promise<string | null> {
  if (_cachedTenantId) return _cachedTenantId;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data } = await supabase
    .from("user_profiles")
    .select("tenant_id")
    .eq("id", user.id)
    .maybeSingle();

  _cachedTenantId = data?.tenant_id || null;
  return _cachedTenantId;
}

// Limpiar cache al cambiar de usuario
supabase.auth.onAuthStateChange((event) => {
  if (event === "SIGNED_OUT" || event === "SIGNED_IN") {
    _cachedTenantId = null;
  }
});

export interface UserProfile {
  id: string;
  code: string;
  name: string;
  phone: string;
  document: string;
  role: string;
  status: string;
  permissions: Record<string, boolean>;
  last_login: string | null;
  tenant_id: string | null;
  created_at: string;
  updated_at: string;
}

export type ProductType = "MATERIA_PRIMA" | "PRODUCTO_TERMINADO";
export type UnitMeasure =
  | "UNIDADES"
  | "LITROS"
  | "GRAMOS"
  | "KILOS"
  | "MILILITROS"
  | "METROS";

export interface ProductVariant {
  id: string;
  size: string | null;
  color: string | null;
  stock: number;
  purchase_price: number;
  sale_price: number;
}

export interface Product {
  id: string;
  code: string;
  name: string;
  // Legacy: mantener por backwards compatibility
  category: string;
  subcategory: string | null;
  // NEW: relaciones a tablas de categorías
  category_id?: string;
  subcategory_id?: string | null;
  size: string | null;
  color: string | null;
  purchase_price: number;
  sale_price: number;
  profit: number;
  profit_percentage: number;
  stock: number;
  min_stock: number;
  discount: number;
  supplier: string | null;
  image_url: string | null;
  product_type: ProductType;
  unit_measure: UnitMeasure;
  batch_number: string | null;
  production_metadata: Record<string, any>;
  variants?: ProductVariant[] | null;
  tenant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: string;
  code: string;
  name: string;
  document: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  total_purchases: number;
  pending_balance: number;
  status: string;
  tenant_id: string | null;
  created_at: string;
}

export interface StockAlert {
  product_id: string;
  product_code: string;
  product_name: string;
  category: string;
  current_stock: number;
  min_stock: number;
  days_until_stockout: number;
  urgency_level: "CRÍTICO" | "URGENTE" | "BAJO" | "ADVERTENCIA";
}

// Stock Alerts Functions
export const getStockAlerts = async (
  thresholdMultiplier: number = 1.2,
): Promise<StockAlert[]> => {
  try {
    const { data, error } = await supabase.rpc("get_low_stock_products", {
      p_threshold_multiplier: thresholdMultiplier,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error al cargar alertas de stock:", error);
    return [];
  }
};
